/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/index";
import { Avatar } from "../WorkDay/shared/Avatar";
import { localDate } from "../WorkDay/shared/dates";
import { ClockCard } from "./ClockCard";
import { MyShifts } from "./MyShifts";
import { OpenShifts } from "./OpenShifts";
import { Preferences } from "./Preferences";
import { TimeOff } from "./TimeOff";
import type { LinkView } from "./types";

type Tab = "shifts" | "preferences" | "time_off";

// A team member's personal rota page, opened from the link in their email —
// no login. See docs/plans/workday/staff_planning.md (phase 2).
export const TeamLinkPage = defineComponent({
  name: "TeamLinkPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const route = useRoute();
    const token = String(route.params.token ?? "");

    const view = ref<LinkView | null>(null);
    const error = ref<string | null>(null);
    const tab = ref<Tab>("shifts");

    const load = async () => {
      try {
        view.value = (await trpc.teamLink.view.query({ token, today: localDate() })) as LinkView;
        error.value = null;
      } catch (e) {
        error.value = (e as { message?: string })?.message ?? t("teamlink.page.failed");
      }
    };
    onMounted(async () => {
      await load();
      if (view.value?.periods.some((p) => !p.submitted_at)) tab.value = "preferences";
    });

    const pendingPrefs = computed(() => view.value?.periods.filter((p) => !p.submitted_at).length ?? 0);

    const TABS: { key: Tab; icon: string }[] = [
      { key: "shifts", icon: "calendar_month" },
      { key: "preferences", icon: "tune" },
      { key: "time_off", icon: "beach_access" },
    ];

    return () => (
      <div class="min-h-screen bg-base-200 pb-24">
        <div class="mx-auto max-w-lg space-y-4 px-4 pt-6">
          {error.value ? (
            <div class="mt-20 rounded-2xl bg-base-100 p-8 text-center">
              <span class="material-symbols-outlined text-5xl text-base-content/30">link_off</span>
              <p class="mt-3 text-base-content/70">{error.value}</p>
            </div>
          ) : !view.value ? (
            <div class="flex justify-center pt-24">
              <Loading />
            </div>
          ) : (
            <>
              <header class="flex items-center gap-3">
                <Avatar name={view.value.me.name} id={view.value.me.id} color={view.value.me.color} size="lg" />
                <div class="min-w-0">
                  <div class="text-sm text-base-content/60">{view.value.me.restaurant_name}</div>
                  <h1 class="truncate text-2xl font-bold">{view.value.me.name}</h1>
                  {view.value.me.roles.length > 0 && <div class="text-xs text-base-content/50">{view.value.me.roles.join(" · ")}</div>}
                </div>
              </header>
              {tab.value === "shifts" && (
                <div class="space-y-6">
                  <ClockCard token={token} view={view.value} onChanged={load} />
                  <OpenShifts token={token} view={view.value} onChanged={load} />
                  <MyShifts token={token} view={view.value} onChanged={load} />
                </div>
              )}
              {tab.value === "preferences" && <Preferences token={token} view={view.value} onSaved={load} />}
              {tab.value === "time_off" && <TimeOff token={token} view={view.value} onSaved={load} />}
            </>
          )}
        </div>
        {view.value && (
          <nav class="fixed inset-x-0 bottom-0 border-t border-base-300 bg-base-100 pb-[env(safe-area-inset-bottom)]">
            <div class="mx-auto grid max-w-lg grid-cols-3">
              {TABS.map((x) => (
                <button
                  key={x.key}
                  class={["relative flex flex-col items-center gap-0.5 py-2 text-xs", tab.value === x.key ? "text-primary" : "text-base-content/60"].join(" ")}
                  onClick={() => (tab.value = x.key)}
                >
                  <span class="material-symbols-outlined">{x.icon}</span>
                  {t(`teamlink.page.tab_${x.key}`)}
                  {x.key === "preferences" && pendingPrefs.value > 0 && <span class="absolute right-1/3 top-1 h-2 w-2 rounded-full bg-error" />}
                </button>
              ))}
            </div>
          </nav>
        )}
      </div>
    );
  },
});
